import React from 'react';
import moment from 'moment';
import { Experiment, ExperimentStatus } from 'apps/experiments/src/types';
import { createTableHelper } from 'common/components/table-react/helpers';
import { useTranslation } from 'common/i18n/hooks';
import { Row } from 'common/components/table-react/types';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { COMPLETION_DATE_STATUS, computeCompletionDate } from 'iron/apps/experiments/src/components/utils';
import { useDuplicateExperiment } from 'iron/apps/experiments/src/components/hooks/duplicate-experiment';
import { useDeleteExperiment } from 'iron/apps/experiments/src/components/hooks/delete-experiment';
import { ExperimentTableCellType } from '../types';
import { EMPTY_CELL_LABEL } from '../custom-cell-types';
const tableHelper = createTableHelper<Experiment>();
const DELETE_ACTION = "delete";
const DUPLICATE_ACTION = "duplicate";
export function useExperimentColumns() {
  const i18n = useTranslation();
  const duplicateExperiment = useDuplicateExperiment({
    isListPage: true
  });
  const deleteExperiment = useDeleteExperiment({
    isListPage: true
  });
  const columns = React.useMemo(() => [tableHelper.accessorColumn("name", {
    id: "name",
    cellType: ExperimentTableCellType.Name,
    headerTitle: i18n.t("experiments.list.column-name"),
    minSize: 100,
    fixedSize: 300,
    enableColumnFilter: false,
    enableSorting: true,
    templateParams: {
      getI18n: () => i18n
    },
    columnSelectionParams: {
      canBeHidden: false,
      isDefault: true
    },
    isPinnedLeft: true
  }), tableHelper.accessorColumn("status", {
    id: "status",
    cellType: ExperimentTableCellType.Status,
    headerTitle: i18n.t("experiments.list.column-status"),
    minSize: 100,
    fixedSize: 200,
    enableColumnFilter: true,
    filterFn: (row: Row<Experiment>, _, filterValue: ExperimentStatus[]) => {
      if (!(filterValue !== null && filterValue !== void 0 && filterValue.length)) {
        return true;
      }
      return filterValue.includes(row.original.status);
    },
    enableSorting: true,
    columnSelectionParams: {
      canBeHidden: false,
      isDefault: true
    },
    isPinnedLeft: true
  }), tableHelper.accessorColumn("results_cache", {
    id: "results_cache",
    cellType: ExperimentTableCellType.Results,
    headerTitle: i18n.t("experiments.list.column-results"),
    minSize: 100,
    fixedSize: 200,
    enableColumnFilter: false,
    enableSorting: false,
    columnSelectionParams: {
      canBeHidden: true,
      isDefault: true
    }
  }), tableHelper.accessorColumn("creator_id", {
    id: "creator_id",
    cellType: ExperimentTableCellType.UserInfo,
    headerTitle: i18n.t("experiments.list.column-creator"),
    minSize: 100,
    fixedSize: 100,
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: (row: Row<Experiment>, _, filterValue: number[]) => {
      if (!(filterValue !== null && filterValue !== void 0 && filterValue.length)) {
        return true;
      }
      return filterValue.includes(row.original.creator_id);
    },
    templateParams: {
      getUserInfo: (row: Row<Experiment>) => ({
        id: row.original.creator_id,
        name: row.original.creator_name,
        email: row.original.creator_email
      })
    },
    columnSelectionParams: {
      canBeHidden: true,
      isDefault: true
    }
  }), tableHelper.accessorColumn(experiment => {
    return experiment.start_date ? i18n.formatDate(moment(experiment.start_date), {
      month: "numeric",
      day: "numeric",
      year: "numeric"
    }) : EMPTY_CELL_LABEL;
  }, {
    id: "start_date",
    cellType: ExperimentTableCellType.LightText,
    headerTitle: i18n.t("experiments.list.column-start-date"),
    minSize: 100,
    fixedSize: 120,
    enableSorting: true,
    enableColumnFilter: false,
    columnSelectionParams: {
      canBeHidden: true,
      isDefault: true
    }
  }), tableHelper.accessorColumn(experiment => {
    const endDate = computeCompletionDate(experiment);
    switch (endDate.status) {
      case COMPLETION_DATE_STATUS.NOT_STARTED:
        return EMPTY_CELL_LABEL;
      case COMPLETION_DATE_STATUS.PENDING_DATA:
        return i18n.t("experiments.list.end-date-no-data");
      case COMPLETION_DATE_STATUS.ESTIMATED:
        return i18n.t("experiments.list.end-date-estimated", {
          date: i18n.formatDate(endDate.date!, {
            month: "numeric",
            day: "numeric",
            year: "numeric"
          })
        });
      case COMPLETION_DATE_STATUS.ACTUAL:
        return endDate.date ? i18n.formatDate(endDate.date, {
          month: "numeric",
          day: "numeric",
          year: "numeric"
        }) : EMPTY_CELL_LABEL;
      default:
        return EMPTY_CELL_LABEL;
    }
  }, {
    id: "end_date",
    cellType: ExperimentTableCellType.LightText,
    headerTitle: i18n.t("experiments.list.column-end-date"),
    minSize: 100,
    fixedSize: 120,
    enableSorting: true,
    enableColumnFilter: false,
    columnSelectionParams: {
      canBeHidden: true,
      isDefault: true
    }
  }), tableHelper.displayColumn({
    id: "actions",
    headerTitle: "",
    fixedSize: 60,
    minSize: 60,
    maxSize: 60,
    isPinnedRight: true,
    cell: context => {
      const experiment = context.row.original;
      const items = [{
        name: DUPLICATE_ACTION,
        label: i18n.t("common.duplicate"),
        icon: "duplicate"
      }];
      if (experiment.can_update_basic || experiment.allow_staff_override && experiment.is_superadmin) {
        items.push({
          name: DELETE_ACTION,
          label: i18n.t("common.delete"),
          icon: "trashcan"
        });
      }
      return <SelectReact backButton menuWidth={200} icon={{
        leftIcon: "hor-ellipsis"
      }} iconOnly theme={SelectTheme.Tertiary} tabIndex={0} sections={[{
        items
      }]} onChange={(ev: TODO) => {
        switch (ev.detail.item.name) {
          case DELETE_ACTION:
            deleteExperiment.mutate({
              id: experiment.id
            });
            break;
          case DUPLICATE_ACTION:
            duplicateExperiment.mutate({
              id: experiment.id
            });
            break;
        }
      }} onClick={ev => {
        ev.stopPropagation();
      }} />;
    }
  })], [i18n]);
  return columns;
}